import { CaptureUpdateAction } from "@excalidraw/excalidraw";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import { isInitializedImageElement } from "@excalidraw/element";
import { useCallback, useEffect, useRef, useState } from "react";

import type { FileId } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { LocalData } from "../data/LocalData";
import {
  createScene,
  deleteScene,
  getScenesIndex,
  renameScene,
  switchScene,
} from "../data/scenes";

import "./SceneSwitcher.scss";

type SceneData = ReturnType<typeof switchScene>;

export const SceneSwitcher = ({
  excalidrawAPI,
  disabled,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  /** e.g. while in a live collaboration room */
  disabled: boolean;
}) => {
  const [index, setIndex] = useState(getScenesIndex);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  const applySceneData = useCallback(
    (data: SceneData) => {
      if (!excalidrawAPI) {
        return;
      }
      excalidrawAPI.updateScene({
        elements: data.elements,
        appState: data.appState ?? getDefaultAppState(),
        captureUpdate: CaptureUpdateAction.NEVER,
      });
      excalidrawAPI.history.clear();

      const loaded = excalidrawAPI.getFiles();
      const fileIds = data.elements.reduce((acc, element) => {
        if (isInitializedImageElement(element) && !loaded[element.fileId]) {
          acc.push(element.fileId);
        }
        return acc;
      }, [] as FileId[]);
      if (fileIds.length) {
        LocalData.fileStorage.getFiles(fileIds).then(({ loadedFiles }) => {
          if (loadedFiles.length) {
            excalidrawAPI.addFiles(loadedFiles);
          }
        });
      }
    },
    [excalidrawAPI],
  );

  const onSwitch = (id: string) => {
    setOpen(false);
    if (id === index.activeId) {
      return;
    }
    LocalData.flushSave();
    applySceneData(switchScene(id));
    setIndex(getScenesIndex());
  };

  const onNew = () => {
    setOpen(false);
    LocalData.flushSave();
    createScene();
    applySceneData({ elements: [], appState: null });
    setIndex(getScenesIndex());
  };

  const onRename = (id: string, current: string) => {
    const name = window.prompt("Rename scene", current);
    if (name !== null) {
      setIndex(renameScene(id, name));
    }
  };

  const onDelete = (id: string, name: string) => {
    if (index.scenes.length === 1) {
      return;
    }
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) {
      return;
    }
    if (id === index.activeId) {
      LocalData.flushSave();
    }
    const data = deleteScene(id);
    if (data) {
      applySceneData(data);
    }
    setIndex(getScenesIndex());
  };

  const active = index.scenes.find((s) => s.id === index.activeId)!;

  return (
    <div className="SceneSwitcher" ref={rootRef}>
      <button
        type="button"
        className="SceneSwitcher__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        title={disabled ? "Unavailable during live collaboration" : "Scenes"}
        onClick={() => {
          setIndex(getScenesIndex());
          setOpen((v) => !v);
        }}
      >
        <span className="SceneSwitcher__name">{active.name}</span>
        <span aria-hidden>▾</span>
      </button>
      {open && (
        <div className="SceneSwitcher__menu" role="listbox">
          {index.scenes.map((scene) => (
            <div className="SceneSwitcher__item" key={scene.id}>
              <button
                type="button"
                role="option"
                aria-selected={scene.id === index.activeId}
                className="SceneSwitcher__select"
                onClick={() => onSwitch(scene.id)}
              >
                {scene.name}
              </button>
              <button
                type="button"
                aria-label={`Rename ${scene.name}`}
                title="Rename"
                onClick={() => onRename(scene.id, scene.name)}
              >
                ✎
              </button>
              <button
                type="button"
                aria-label={`Delete ${scene.name}`}
                title="Delete"
                disabled={index.scenes.length === 1}
                onClick={() => onDelete(scene.id, scene.name)}
              >
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="SceneSwitcher__new" onClick={onNew}>
            + New scene
          </button>
        </div>
      )}
    </div>
  );
};
